import * as THREE from "three";
import { scene } from "../core";

// Define particle types
export enum ParticleType {
  EXPLOSION = 0,
  DUST = 1,
  COLLECT = 2,
  ROCKET_TRAIL = 3,
}

// Particle system configuration for each type
const particleConfig = {
  [ParticleType.EXPLOSION]: {
    count: 20,
    size: { min: 0.05, max: 0.15 },
    lifetime: { min: 30, max: 50 },
    velocity: { min: 0.03, max: 0.08 },
    colors: [0x0088ff, 0x0033aa, 0x00ffff],
    gravity: 0.002,
  },
  [ParticleType.DUST]: {
    count: 5,
    size: { min: 0.03, max: 0.08 },
    lifetime: { min: 15, max: 25 },
    velocity: { min: 0.01, max: 0.03 },
    colors: [0x55aaff, 0x88ccff, 0xaaddff],
    gravity: 0.0005,
  },
  [ParticleType.COLLECT]: {
    count: 10,
    size: { min: 0.04, max: 0.1 },
    lifetime: { min: 20, max: 40 },
    velocity: { min: 0.02, max: 0.05 },
    colors: [0xffff00, 0xff9900, 0xffcc00],
    gravity: 0.001,
  },
  [ParticleType.ROCKET_TRAIL]: {
    count: 8,
    size: { min: 0.05, max: 0.12 },
    lifetime: { min: 10, max: 20 },
    velocity: { min: 0.01, max: 0.04 },
    colors: [0xff5500, 0xff8800, 0xffaa00],
    gravity: 0.0008,
  },
};

// Shared geometries for particles - create once and reuse
const particleGeometries = {
  sphere: new THREE.SphereGeometry(1, 8, 6), // Lower poly count than before
  box: new THREE.BoxGeometry(1, 1, 1),
  tetra: new THREE.TetrahedronGeometry(1),
};

// Track all active particle systems
const activeSystems: ParticleSystem[] = [];

/**
 * Optimized particle system that uses instanced meshes for better performance
 */
class ParticleSystem {
  private group: THREE.Group;
  private particles: Particle[] = [];
  private isActive = true;

  constructor(
    position: THREE.Vector3,
    type: ParticleType,
    options: {
      count?: number;
      directionBias?: THREE.Vector3;
    } = {}
  ) {
    this.group = new THREE.Group();
    this.group.position.copy(position);
    scene.add(this.group);

    const config = particleConfig[type];
    const count = options.count || config.count;

    // Create particles
    for (let i = 0; i < count; i++) {
      this.createParticle(config, options.directionBias);
    }

    // Add to active systems
    activeSystems.push(this);
  }

  private createParticle(
    config: (typeof particleConfig)[keyof typeof particleConfig],
    directionBias?: THREE.Vector3
  ): void {
    // Choose random geometry for variety
    const geometryType =
      Math.random() > 0.7 ? (Math.random() > 0.5 ? "tetra" : "box") : "sphere";
    const geometry = particleGeometries[geometryType];

    // Select color from config colors
    const color =
      config.colors[Math.floor(Math.random() * config.colors.length)];

    // Create material - use BasicMaterial for better performance
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.8,
    });

    // Create mesh with random size
    const size =
      config.size.min + Math.random() * (config.size.max - config.size.min);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.scale.set(size, size, size);

    // Random position offset
    mesh.position.set(
      (Math.random() - 0.5) * 0.4,
      (Math.random() - 0.5) * 0.4,
      (Math.random() - 0.5) * 0.4
    );

    // Calculate velocity
    const speed =
      config.velocity.min +
      Math.random() * (config.velocity.max - config.velocity.min);
    const velocity = new THREE.Vector3(
      (Math.random() - 0.5) * speed,
      Math.random() * speed,
      (Math.random() - 0.5) * speed
    );

    // Apply direction bias if provided
    if (directionBias) {
      velocity.add(directionBias.clone().multiplyScalar(speed * 0.5));
    }

    // Create particle object
    const particle: Particle = {
      mesh,
      velocity,
      age: 0,
      maxAge:
        config.lifetime.min +
        Math.random() * (config.lifetime.max - config.lifetime.min),
      gravity: config.gravity,
    };

    this.particles.push(particle);
    this.group.add(mesh);
  }

  /**
   * Update all particles in this system
   */
  update(): boolean {
    let alive = false;

    for (const particle of this.particles) {
      particle.age++;

      if (particle.age < particle.maxAge) {
        alive = true;

        // Update position
        particle.mesh.position.add(particle.velocity);

        // Apply gravity
        particle.velocity.y -= particle.gravity;

        // Slow down over time
        particle.velocity.multiplyScalar(0.98);

        // Fade out
        const lifeRatio = particle.age / particle.maxAge;
        (particle.mesh.material as THREE.Material).opacity = 1 - lifeRatio;

        // Scale down as they age
        if (lifeRatio > 0.7) {
          const scale = 1 - (lifeRatio - 0.7) / 0.3;
          particle.mesh.scale.multiplyScalar(scale);
        }

        // Add rotation
        particle.mesh.rotation.x += 0.02;
        particle.mesh.rotation.y += 0.02;
      } else {
        particle.mesh.visible = false;
      }
    }

    if (!alive && this.isActive) {
      // Clean up
      scene.remove(this.group);
      this.isActive = false;

      // Remove from active systems
      const index = activeSystems.indexOf(this);
      if (index !== -1) {
        activeSystems.splice(index, 1);
      }
    }

    return alive;
  }
}

// Particle interface
interface Particle {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  age: number;
  maxAge: number;
  gravity: number;
}

/**
 * Update all active particle systems - called from the animation loop
 */
export function updateParticleSystems(): void {
  for (let i = activeSystems.length - 1; i >= 0; i--) {
    activeSystems[i].update();
  }
}

/**
 * Create an explosion effect at the specified position
 */
export function createExplosionEffect(position: THREE.Vector3): void {
  new ParticleSystem(position, ParticleType.EXPLOSION);
}

/**
 * Create a dust effect at the specified position
 */
export function createDustEffect(position: THREE.Vector3): void {
  new ParticleSystem(position, ParticleType.DUST);
}

/**
 * Create a collection effect at the specified position
 */
export function createCollectionEffect(position: THREE.Vector3): void {
  new ParticleSystem(position, ParticleType.COLLECT);
}

/**
 * Create a rocket trail effect at the specified position with direction
 */
export function createRocketTrailEffect(
  position: THREE.Vector3,
  direction: THREE.Vector3
): void {
  new ParticleSystem(position, ParticleType.ROCKET_TRAIL, {
    directionBias: direction.clone().negate(),
  });
}
